// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

export const DRAFT_KEY_PREFIX = "riddle.draft:";
const DRAFT_ACCOUNT_KEY = "riddle.draftOwnerAccount";
const DRAFT_TENANT_KEY = "riddle.draftOwnerTenant";

export interface DraftOwner {
  homeAccountId: string;
  tenantId: string;
}

interface DraftOwnerState {
  lastAccount: string | undefined;
  lastTenant: string | undefined;
  signedInAccount: string | undefined;
  confirmedTenant: string | undefined;
}

let ownerState: DraftOwnerState = {
  lastAccount: undefined,
  lastTenant: undefined,
  signedInAccount: undefined,
  confirmedTenant: undefined,
};
let hydrated = false;
let restoreOwner: DraftOwner | undefined;
let saveOwner: DraftOwner | undefined;
let signedOutOwner: DraftOwner | undefined;
const listeners = new Set<() => void>();

function normalize(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function readStored(key: string): string | undefined {
  try {
    return normalize(window.sessionStorage.getItem(key));
  } catch {
    return undefined;
  }
}

function writeStored(key: string, value: string | undefined): void {
  try {
    if (value === undefined) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, value);
    }
  } catch {
  }
}

function toOwner(homeAccountId: string | undefined, tenantId: string | undefined): DraftOwner | undefined {
  return homeAccountId !== undefined && tenantId !== undefined ? { homeAccountId, tenantId } : undefined;
}

function sameOwner(left: DraftOwner | undefined, right: DraftOwner | undefined): boolean {
  return left?.homeAccountId === right?.homeAccountId && left?.tenantId === right?.tenantId;
}

function hydrate(): void {
  if (hydrated) {
    return;
  }
  hydrated = true;
  ownerState = {
    ...ownerState,
    lastAccount: ownerState.lastAccount ?? readStored(DRAFT_ACCOUNT_KEY),
    lastTenant: ownerState.lastTenant ?? readStored(DRAFT_TENANT_KEY),
  };
  saveOwner = toOwner(ownerState.lastAccount, ownerState.lastTenant);
}

function publish(next: DraftOwnerState): void {
  ownerState = next;
  writeStored(DRAFT_ACCOUNT_KEY, next.lastAccount);
  writeStored(DRAFT_TENANT_KEY, next.lastTenant);
  const nextSave = toOwner(next.lastAccount, next.lastTenant);
  const nextRestore =
    next.signedInAccount === next.lastAccount && next.confirmedTenant === next.lastTenant
      ? nextSave
      : undefined;
  const nextSignedOut = next.confirmedTenant === next.lastTenant ? nextSave : undefined;
  const changed =
    !sameOwner(nextSave, saveOwner) ||
    !sameOwner(nextRestore, restoreOwner) ||
    !sameOwner(nextSignedOut, signedOutOwner);
  saveOwner = sameOwner(nextSave, saveOwner) ? saveOwner : nextSave;
  restoreOwner = sameOwner(nextRestore, restoreOwner) ? restoreOwner : nextRestore;
  signedOutOwner = sameOwner(nextSignedOut, signedOutOwner) ? signedOutOwner : nextSignedOut;
  if (changed) {
    listeners.forEach((listener) => listener());
  }
}

export function getDraftOwner(): DraftOwner | undefined {
  hydrate();
  return restoreOwner;
}

export function getSignedOutDraftOwner(): DraftOwner | undefined {
  hydrate();
  return signedOutOwner;
}

export function getDraftSaveOwner(): DraftOwner | undefined {
  hydrate();
  return saveOwner;
}

export function getLastKnownTenantId(): string | undefined {
  hydrate();
  return ownerState.lastTenant;
}

export function subscribeToDraftOwner(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function clearDraftKeys(storage: Storage): void {
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(DRAFT_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
  }
}

export function clearAllDrafts(): void {
  clearDraftKeys(window.sessionStorage);
  clearDraftKeys(window.localStorage);
}

export function noteDraftAccount(homeAccountId: string | null | undefined): void {
  hydrate();
  const account = normalize(homeAccountId);
  if (account === undefined) {
    publish({ ...ownerState, signedInAccount: undefined });
    return;
  }
  if (ownerState.lastAccount !== undefined && ownerState.lastAccount !== account) {
    clearAllDrafts();
    publish({ lastAccount: account, lastTenant: undefined, signedInAccount: account, confirmedTenant: undefined });
    return;
  }
  publish({ ...ownerState, lastAccount: account, signedInAccount: account });
}

export function noteDraftTenant(tenantId: string | null | undefined): void {
  hydrate();
  const tenant = normalize(tenantId);
  if (tenant === undefined) {
    return;
  }
  if (ownerState.lastTenant !== undefined && ownerState.lastTenant !== tenant) {
    clearAllDrafts();
  }
  publish({ ...ownerState, lastTenant: tenant, confirmedTenant: tenant });
}

export function forgetDraftOwner(): void {
  hydrated = true;
  clearAllDrafts();
  publish({
    lastAccount: undefined,
    lastTenant: undefined,
    signedInAccount: undefined,
    confirmedTenant: undefined,
  });
}
